import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { instanceDetailsTarget } from "@/components/personal-apps/discovery/discoveryUrl";
import { useEffect, useMemo, useRef, useState } from "react";
import { launchPreparedApplication } from "@/api/portableApplicationClient";
import { computeConnectionError } from "@/api/computeConnectionsClient";
import { isProductionFeatureProfile } from "@/app/featureProfile";
import { computeClient } from "./computeClient";
import type { PersonalApp, PreparedImport } from "./types";

// This adapter is presentation only. It selects the accepted immutable app;
// it does not alter/rebuild its bundle, requirements, Model Sets or workflow.
export const WAN_BASELINE = {
  bundle_sha256:
    "sha256:929aa3dcc1aa89c7210e1b24520c082871d242636b942772168616dff4afefe5",
  root_contract_ref:
    "sha256:e2ccef685f593b9f44772e467b86d6b3ff4daf9dd433ec0217a50c210e0a2fcb",
  derivation_ref:
    "sha256:b186d8d2511c5dd62a703b8165e09b86e62c7611e9ceec79f40935bad38ecd0d",
};
// The Replace-capable bundle is a separate immutable app. Animation's accepted
// bundle above is not rebuilt or re-pinned.
export const WAN_MODES_BASELINE = {
  bundle_sha256:
    "sha256:776f16463929ec7d72925537b3f0097cdd34b69c3ef06857950492caebac6aa6",
  root_contract_ref:
    "sha256:e2ccef685f593b9f44772e467b86d6b3ff4daf9dd433ec0217a50c210e0a2fcb",
  derivation_ref:
    "sha256:17288d7b6964e71cbd506f93c97cc7980cc757f175f4e3984663ee393d41de3e",
};

export type WanMode = {
  id: "animation" | "replace";
  title: string;
  action: string;
  generate_path: string;
  output_key: string;
};

const WAN_INPUTS = [
  {
    label: "Reference image",
    filename: "image.jpeg",
    content_type: "image/jpeg",
  },
  { label: "Source video", filename: "video.mp4", content_type: "video/mp4" },
];

const ANIMATION_MODE: WanMode = {
  id: "animation",
  title: "Wan Animation",
  action: "Generate animation",
  generate_path: "/generate",
  output_key: "animation.mp4",
};
const MULTI_ANIMATION_MODE: WanMode = {
  ...ANIMATION_MODE,
  generate_path: "/generate/animation",
};
const REPLACE_MODE: WanMode = {
  id: "replace",
  title: "Wan Replace",
  action: "Generate replace",
  generate_path: "/generate/replace",
  output_key: "replace.mp4",
};

function matchesBaseline(
  p: PreparedImport | null | undefined,
  baseline: typeof WAN_BASELINE,
) {
  return (
    p?.bundle_sha256 === baseline.bundle_sha256 &&
    p.root_contract_ref === baseline.root_contract_ref &&
    p.derivation_ref === baseline.derivation_ref
  );
}

/** The modes this Instance may use. Empty means no preparation UI. */
export function wanPreparationModes(app: PersonalApp): WanMode[] {
  if (
    isProductionFeatureProfile() ||
    app.role !== "owner"
  )
    return [];
  const p = app.prepared_import;
  if (matchesBaseline(p, WAN_BASELINE)) return [ANIMATION_MODE];
  if (matchesBaseline(p, WAN_MODES_BASELINE))
    return [MULTI_ANIMATION_MODE, REPLACE_MODE];
  return [];
}

export function hasWanPreparation(app: PersonalApp) {
  return wanPreparationModes(app).length > 0;
}

export function verifiedPreparedRun(
  p: PreparedImport,
  expected: PreparedImport,
  runId: string,
) {
  const r = p.verification_receipt;
  return (
    p.run_id === runId &&
    p.lease_status === "ready" &&
    !p.stop_requested_at &&
    (!p.lease_expires_at || Date.parse(p.lease_expires_at) > Date.now()) &&
    p.verification_status === "verified" &&
    r?.fully_satisfied === true &&
    p.bundle_sha256 === expected.bundle_sha256 &&
    p.root_contract_ref === expected.root_contract_ref &&
    p.derivation_ref === expected.derivation_ref &&
    r.bundle_sha256 === expected.bundle_sha256 &&
    r.contract_ref === expected.root_contract_ref &&
    r.derivation_ref === expected.derivation_ref &&
    r.execution?.run_id === runId &&
    r.execution?.lease_id === p.lease_id
  );
}

export default function ProcessPreparation({
  app,
  onNavigate,
}: {
  app: PersonalApp;
  onNavigate?: (path: string) => void;
}) {
  useLocale();
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [modeId, setModeId] = useState<WanMode["id"]>(
    () => wanPreparationModes(app)[0]?.id ?? "animation",
  );
  const modes = useMemo(() => wanPreparationModes(app), [app]);
  const mode = modes.find((candidate) => candidate.id === modeId) ?? modes[0];
  const frame = useRef<HTMLIFrameElement>(null);
  const channel = useMemo(
    () => crypto.randomUUID(),
    [app.instance_id, mode?.id],
  );
  const origin = new URL(app.app_url).origin;
  const descriptor = mode && {
    title: mode.title,
    action: mode.action,
    inputs: WAN_INPUTS,
    status_path: "/status",
    generate_path: mode.generate_path,
    output_key: mode.output_key,
    mode: mode.id,
  };
  const url = `${origin}/__ato/preparation?${new URLSearchParams({ descriptor: JSON.stringify(descriptor), channel })}`;

  useEffect(() => {
    const prepared = app.prepared_import!;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let busy = false;
    let runId: string | null = null;
    let sequence = 0;
    const send = (type: string, values = {}) => {
      if (!controller.signal.aborted)
        frame.current?.contentWindow?.postMessage(
          { channel, type, ...values },
          origin,
        );
    };
    const poll = async (token: number) => {
      try {
        const current = (await computeClient.get(app.instance_id)).instance
          .prepared_import;
        if (controller.signal.aborted || token !== sequence) return;
        if (
          !current ||
          current.run_id !== runId ||
          current.verification_status === "failed" ||
          ["failed", "stopped", "expired", "revoked", "cancelled"].includes(
            current.lease_status || "",
          )
        ) {
          busy = false;
          setLocked(false);
          send("failed");
          return;
        }
        if (runId && verifiedPreparedRun(current, prepared, runId)) {
          send("ready", { run_id: runId });
          return;
        }
        if (["preparing", "running"].includes(current.lease_status || "")) {
          send("starting", { run_id: runId, loading: true });
        }
        timer = setTimeout(() => void poll(token), 3000);
      } catch {
        if (!controller.signal.aborted && token === sequence)
          timer = setTimeout(() => void poll(token), 5000);
      }
    };
    const launch = async (token: number, deadline: number) => {
      try {
        const result = await launchPreparedApplication(
          prepared,
          controller.signal,
        );
        if (controller.signal.aborted || token !== sequence) return;
        if (
          !("import" in result) ||
          result.import.instance.instance_id !== app.instance_id ||
          !result.import.run_id
        )
          throw new Error("prepared_instance_mismatch");
        runId = result.import.run_id;
        send("starting", { run_id: runId });
        void poll(token);
      } catch (error) {
        if (controller.signal.aborted || token !== sequence) return;
        const pending = error as { status?: number; code?: string };
        if (
          pending.status === 409 &&
          pending.code === "runner_provisioning" &&
          Date.now() < deadline
        ) {
          timer = setTimeout(() => void launch(token, deadline), 5000);
          return;
        }
        busy = false;
        setLocked(false);
        if (
          pending.code?.startsWith("compute_") ||
          [
            "on_demand_machine_unavailable",
            "provision_capacity_exhausted",
          ].includes(pending.code ?? "")
        ) {
          const message = computeConnectionError(error);
          setConnectionError(message);
          send("failed", { message });
          return;
        }
        send("failed");
      }
    };
    const message = async (event: MessageEvent) => {
      if (
        event.origin !== origin ||
        event.source !== frame.current?.contentWindow ||
        event.data?.channel !== channel
      )
        return;
      const m = event.data;
      if (m.type === "connect") {
        send("connected");
        return;
      }
      if (m.type === "uploading") {
        setUploading(true);
        return;
      }
      if (m.type === "uploaded") {
        setUploading(false);
        return;
      }
      if (m.type === "launch") {
        if (busy) return;
        setConnectionError(null);
        setLocked(true);
        busy = true;
        const token = ++sequence;
        void launch(token, Date.now() + 15 * 60 * 1000);
      } else if (m.type === "stop") {
        const cancelled = m.cancel === true;
        try {
          const current = (await computeClient.get(app.instance_id)).instance
            .prepared_import;
          if (
            !current ||
            !runId ||
            current.run_id !== runId ||
            (m.run_id && m.run_id !== runId)
          )
            throw new Error("prepared_run_mismatch");
          ++sequence;
          clearTimeout(timer);
          await computeClient.stop(app.instance_id);
          busy = false;
          setLocked(false);
          send("stopped", { run_id: runId, cancel: cancelled });
        } catch {
          send("failed");
        }
      }
    };
    window.addEventListener("message", message);
    return () => {
      controller.abort();
      clearTimeout(timer);
      window.removeEventListener("message", message);
    };
  }, [app, origin, channel]);

  if (!mode) return null;
  return (
    <>
      {modes.length > 1 && (
        <div role="group" aria-label={t("Workflow")}>
          {modes.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              className={
                candidate.id === mode.id
                  ? "settings-button settings-button--primary"
                  : "settings-button"
              }
              aria-pressed={candidate.id === mode.id}
              disabled={locked || uploading}
              onClick={() => {
                if (locked || uploading) return;
                setConnectionError(null);
                setModeId(candidate.id);
              }}
            >
              {t(candidate.title)}
            </button>
          ))}
        </div>
      )}
      {connectionError && (
        <div role="alert">
          <p>{connectionError}</p>
          <button
            className="settings-button"
            onClick={() =>
              onNavigate?.(
                `/settings/compute?return_to=${encodeURIComponent(instanceDetailsTarget(app.instance_id, "app"))}`,
              )
            }
          ><Message message={"Open Compute settings"} /></button>
        </div>
      )}
      <iframe
        key={mode.id}
        ref={frame}
        src={url}
        title={t(mode.title)}
        sandbox="allow-scripts allow-same-origin"
        className="process-preparation-frame"
        style={{
          width: "100%",
          minHeight: "900px",
          border: 0,
          borderRadius: 12,
        }}
      />
    </>
  );
}
